import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ServiceDetailCard from './components/ServiceDetailCard';
import ComparisonTable from './components/ComparisonTable';
import BeforeAfterGallery from './components/BeforeAfterGallery';
import { detailedServices } from '@/mocks/leistungen';
import { useJsonLd } from '@/hooks/useJsonLd';
import { usePageMeta } from '@/hooks/usePageMeta';

const leistungenReviews = [
  {
    id: 'r1',
    name: 'Katharina M.',
    treatment: 'Smile Premium',
    price: '199€',
    stars: 5,
    text: 'Ich hab vorher echt gezögert, weil ich sehr empfindliche Zähne habe. Bei Jenny hatte ich null Schmerzen — absolut null. Das Ergebnis war direkt nach der Anwendung sichtbar, in gerade mal 60 Minuten. Absolut jeden Cent wert!',
    date: '2026-03-28',
  },
  {
    id: 'r2',
    name: 'Lena S.',
    treatment: 'Smile Classic',
    price: '149€',
    stars: 5,
    text: '45 Minuten, entspannt auf einer bequemen Liege — und danach Zähne, die ich kaum wiedererkannt hab. Kein Brennen, kein Ziehen, nichts. Für 149€ ein unglaubliches Ergebnis. Mein Freund hat es sofort bemerkt!',
    date: '2026-03-05',
  },
  {
    id: 'r3',
    name: 'Sophie T.',
    treatment: 'Smile Starter',
    price: '99€',
    stars: 5,
    text: 'Ich wollte meine Zähne vor der Hochzeit aufhellen — nicht mega-weiß, sondern natürlich strahlend. Jenny hat das genau so umgesetzt. Für 99€ ein perfektes Ergebnis. Genau das, was ich wollte.',
    date: '2026-01-15',
  },
  {
    id: 'r4',
    name: 'Julia R.',
    treatment: 'Fresh-Up',
    price: '69€',
    stars: 5,
    text: 'Ich komme alle 8 Wochen für den Fresh-Up — für 69€ ist das einfach unschlagbar. Meine Zähne bleiben konstant hell und ich bin jedes Mal begeistert wie schnell und angenehm die Anwendung ist.',
    date: '2026-04-01',
  },
];

export default function Leistungen() {
  const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

  usePageMeta({ canonical: '/leistungen' });

  useJsonLd([
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Kosmetische Zahnaufhellung Anwendungen – Flow & Glow Köln',
      url: `${siteUrl || 'https://flowundglow.de'}/leistungen`,
      description: 'Alle Anwendungen für kosmetische Zahnaufhellung bei Flow & Glow in Köln. Peroxidfrei, schmerzfrei, professionell.',
      itemListElement: detailedServices.map((s, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Service',
          name: s.name,
          description: s.description,
          provider: {
            '@type': 'HealthAndBeautyBusiness',
            name: 'Flow & Glow',
            address: { '@type': 'PostalAddress', addressLocality: 'Köln', addressCountry: 'DE' },
          },
          offers: {
            '@type': 'Offer',
            price: s.price.replace('€', '').replace('ab ', '').trim(),
            priceCurrency: 'EUR',
            availability: 'https://schema.org/InStock',
            url: `${siteUrl || 'https://flowundglow.de'}/leistungen`,
            seller: {
              '@type': 'HealthAndBeautyBusiness',
              name: 'Flow & Glow',
            },
          },
          review: leistungenReviews
            .filter((r) => r.treatment === s.name)
            .map((r) => ({
              '@type': 'Review',
              author: { '@type': 'Person', name: r.name },
              datePublished: r.date,
              reviewRating: { '@type': 'Rating', ratingValue: r.stars, bestRating: 5 },
              reviewBody: r.text,
            })),
        },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'HealthAndBeautyBusiness',
      name: 'Flow & Glow – Kosmetische Zahnaufhellung Köln',
      url: siteUrl || 'https://flowundglow.de',
      address: { '@type': 'PostalAddress', addressLocality: 'Köln', addressCountry: 'DE' },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '5.0',
        reviewCount: '47',
        bestRating: '5',
      },
      review: leistungenReviews.map((r) => ({
        '@type': 'Review',
        author: { '@type': 'Person', name: r.name },
        datePublished: r.date,
        reviewRating: { '@type': 'Rating', ratingValue: r.stars, bestRating: 5 },
        reviewBody: r.text,
        name: r.treatment,
      })),
    },
  ]);

  return (
    <main className="font-sans">
      <Navbar />

      {/* Hero */}
      <section className="relative bg-charcoal pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://public.readdy.ai/ai/img_res/a76d6c57ce56fea0aa1d157af78be172.jpg"
            alt="Leistungen Flow & Glow – Kosmetische Zahnaufhellung Köln"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
          <span className="font-sans text-xs tracking-widest uppercase text-rose font-semibold">
            Unsere Anwendungen
          </span>
          <h1 className="font-serif text-5xl md:text-6xl font-medium text-white mt-3 mb-5">
            Dein Wunsch-Lächeln
          </h1>
          <p className="font-sans text-base md:text-lg text-white/60 max-w-xl mx-auto leading-relaxed">
            Von der schnellen Auffrischung bis zur professionellen <strong className="text-white/80">kosmetischen Zahnaufhellung Köln</strong> — die passende Anwendung für jedes Lächeln, schmerzfrei und ohne Peroxid.
          </p>
        </div>
      </section>

      {/* Services list */}
      <section className="bg-offwhite py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {detailedServices.map((service, index) => (
            <ServiceDetailCard key={service.id} service={service} index={index} />
          ))}
        </div>
      </section>

      {/* Before / After Gallery */}
      <BeforeAfterGallery />

      {/* Wirkstoffe Info Block */}
      <section className="bg-white py-10 md:py-12">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="rounded-2xl border border-rose/20 px-7 py-7" style={{ background: '#faf7f3' }}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-rose/15 rounded-xl flex-shrink-0 mt-0.5">
                <i className="ri-flask-line text-rose text-lg" />
              </div>
              <div>
                <h3 className="font-sans text-sm font-semibold text-charcoal mb-2">
                  Unsere Wirkstoffe
                </h3>
                <p className="font-sans text-sm text-charcoal/70 leading-relaxed">
                  Wir arbeiten ausschließlich mit <strong className="text-charcoal">PAP (Phthalimidoperoxycapronsäure)</strong> und <strong className="text-charcoal">Natriumpercarbonat</strong> — beide Wirkstoffe sind komplett peroxidfrei beziehungsweise enthalten weniger als 0,1&nbsp;% Wasserstoffperoxid. Unsere Produkte entsprechen der EU-Kosmetikverordnung und sind frei verkäuflich für kosmetische Anwendungen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <ComparisonTable />

      {/* Reviews with Prices */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="font-sans text-xs tracking-widest uppercase text-rose font-semibold">Kund*innenstimmen</span>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-charcoal mt-3 mb-3">
              Was unsere Kund*innen sagen
            </h2>
            <div className="flex items-center justify-center gap-1.5 mb-3">
              {[1,2,3,4,5].map((s) => (
                <span key={s} className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-star-fill text-amber-400 text-lg" />
                </span>
              ))}
              <span className="font-sans text-sm text-charcoal/60 ml-2">5,0 · 47 Bewertungen</span>
            </div>
            <p className="font-sans text-sm text-charcoal/55 max-w-md mx-auto">
              Echte Erfahrungen zu unseren Anwendungen — inkl. Preisangaben für maximale Transparenz.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {leistungenReviews.map((review) => (
              <article
                key={review.id}
                className="bg-offwhite rounded-2xl p-6 ring-1 ring-charcoal/8 flex flex-col gap-4"
                itemScope
                itemType="https://schema.org/Review"
              >
                {/* Stars + Treatment + Price */}
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-0.5 mb-1.5">
                      {Array.from({ length: review.stars }).map((_, i) => (
                        <span key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-amber-400 text-sm" />
                        </span>
                      ))}
                    </div>
                    <span
                      className="inline-block font-sans text-xs font-semibold text-rose bg-rose/10 px-2.5 py-1 rounded-full"
                      itemProp="name"
                    >
                      {review.treatment}
                    </span>
                  </div>
                  <span className="font-serif text-lg font-medium text-charcoal whitespace-nowrap">
                    {review.price}
                  </span>
                </div>

                {/* Review text */}
                <p
                  className="font-sans text-sm text-charcoal/70 leading-relaxed flex-1"
                  itemProp="reviewBody"
                >
                  &ldquo;{review.text}&rdquo;
                </p>

                {/* Author */}
                <div className="flex items-center gap-2.5 pt-2 border-t border-charcoal/8">
                  <div className="w-8 h-8 flex items-center justify-center bg-rose/15 rounded-full flex-shrink-0">
                    <i className="ri-user-smile-line text-rose text-sm" />
                  </div>
                  <div>
                    <p className="font-sans text-sm font-semibold text-charcoal" itemProp="author">{review.name}</p>
                    <p className="font-sans text-xs text-charcoal/40">{new Date(review.date).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-blush py-16">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-medium text-charcoal mb-4">
            Unsicher, welche Anwendung passt?
          </h2>
          <p className="font-sans text-base text-charcoal/65 mb-8 leading-relaxed">
            Kein Problem — beim kostenlosen Beratungsgespräch finden wir gemeinsam die beste Lösung für dein Lächeln.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://simplybook.me"
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-rose hover:bg-rose-dark text-white font-sans font-medium text-base px-8 py-4 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-calendar-line text-base" />
              </span>
              Beratungstermin buchen
            </a>
            <a
              href="https://wa.me/4922100000"
              rel="nofollow noreferrer"
              className="inline-flex items-center justify-center gap-2 border border-charcoal text-charcoal hover:bg-charcoal/5 font-sans font-medium text-base px-8 py-4 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-base" />
              </span>
              Frage per WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Legal Disclaimer Block */}
      <section className="bg-offwhite py-10 md:py-12">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="rounded-2xl border border-charcoal/10 px-7 py-7" style={{ background: '#faf7f3' }}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-charcoal/8 rounded-xl flex-shrink-0 mt-0.5">
                <i className="ri-information-line text-charcoal/60 text-lg" />
              </div>
              <div>
                <h3 className="font-sans text-sm font-semibold text-charcoal mb-2">
                  Wichtiger Hinweis
                </h3>
                <p className="font-sans text-xs text-charcoal/65 leading-relaxed">
                  Flow &amp; Glow bietet ausschließlich kosmetische Zahnaufhellung an. Unsere Anwendungen sind keine medizinischen Behandlungen und ersetzen keinen Zahnarztbesuch. Wir verwenden ausschließlich peroxidfreie Produkte (PAP-Technologie und Natriumpercarbonat) gemäß EU-Kosmetikverordnung 1223/2009/EG. Vor jeder Anwendung empfehlen wir eine zahnärztliche Kontrolle. Bei Karies, Zahnfleischentzündungen oder anderen zahnmedizinischen Problemen wende dich bitte zuerst an deinen Zahnarzt.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
